import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { usePlans } from "../../plans/PlansContext";
import { useInventory } from "../../inventory/InventoryContext";
import { planLines, planTotals, formatAmount, formatCount } from "../../plans/planMath";
import { timeAgo, formatDateTime } from "../../inventory/time";
import Icon from "../../components/ui/Icon";
import Modal from "../../components/ui/Modal";
import "../inventory/InventoryPage.css";
import "./Plans.css";

export default function PlansPage() {
  const { plans, createPlan, deletePlan, duplicatePlan } = usePlans();
  const { channels } = useInventory();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null);

  const channelsById = useMemo(() => new Map(channels.map((c) => [c.id, c])), [channels]);

  const rows = useMemo(
    () =>
      plans.map((plan) => {
        const lines = planLines(plan, channelsById);
        return { plan, totals: planTotals(plan, lines) };
      }),
    [plans, channelsById]
  );

  const newPlan = () => navigate(`/plans/${createPlan().id}`);

  return (
    <div className="plans">
      <div className="inv-head">
        <div>
          <h1>Media plans</h1>
          <p>Pick channels in the inventory, set a budget, and send the client a plan.</p>
        </div>
        <div className="inv-actions">
          <button type="button" className="btn-primary" onClick={newPlan}>
            <Icon name="plus" /> New plan
          </button>
        </div>
      </div>

      <div className="inv-card">
        {plans.length === 0 ? (
          <div className="inv-empty">
            <h2>Build your first plan</h2>
            <p>
              Select channels in the inventory and choose <strong>Add to plan</strong>, or start an empty
              plan here.
            </p>
            <div className="inv-empty-actions">
              <Link to="/inventory" className="btn">
                Go to the inventory
              </Link>
              <button type="button" className="btn-primary" onClick={newPlan}>
                <Icon name="plus" /> New plan
              </button>
            </div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="inv-table plans-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th className="num">Channels</th>
                  <th className="num">Budget</th>
                  <th className="num">Est. impressions</th>
                  <th>Updated</th>
                  <th className="col-actions">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ plan, totals }) => (
                  <tr key={plan.id}>
                    <td>
                      <Link to={`/plans/${plan.id}`} className="plan-link">
                        {plan.name}
                      </Link>
                      {plan.client && <div className="plan-client">{plan.client}</div>}
                    </td>
                    <td>
                      <span className={`status status-${plan.status.toLowerCase()}`}>{plan.status}</span>
                    </td>
                    <td className="num">{totals.channels}</td>
                    <td className="num">
                      {totals.byCurrency.length === 0 && "—"}
                      {totals.byCurrency.map((c) => (
                        <div key={c.currency}>
                          {formatAmount(c.allocated, c.currency)}
                          {c.planned > 0 && (
                            <div className="plan-client">of {formatAmount(c.planned, c.currency)}</div>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="num">{formatCount(totals.impressions)}</td>
                    <td title={formatDateTime(plan.updatedAt)}>
                      <div className="cell-updated">
                        <span>{timeAgo(plan.updatedAt)}</span>
                        <small>{plan.updatedBy}</small>
                      </div>
                    </td>
                    <td className="col-actions">
                      <div className="row-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          title="Duplicate"
                          aria-label={`Duplicate ${plan.name}`}
                          onClick={() => navigate(`/plans/${duplicatePlan(plan.id).id}`)}
                        >
                          <Icon name="copy" />
                        </button>
                        <button
                          type="button"
                          className="icon-btn danger"
                          title="Delete"
                          aria-label={`Delete ${plan.name}`}
                          onClick={() => setConfirm(plan)}
                        >
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {confirm && (
        <Modal
          title="Delete plan?"
          onClose={() => setConfirm(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={() => {
                  deletePlan(confirm.id);
                  setConfirm(null);
                }}
              >
                Delete plan
              </button>
            </>
          }
        >
          <p className="modal-text">
            <strong>{confirm.name}</strong> will be removed. The channels in the inventory aren't affected.
          </p>
        </Modal>
      )}
    </div>
  );
}
